import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Grid } from "@mui/material";
import { useFeatureFlags } from "@/utils/featureFlags/useFeatureFlags";
import UserDetails from "./profile/UserDetails";
import { Lenders } from "./Lenders";
import UserRolesList from "./UserRolesList";
import Templates from "./Templates";
import BillingWrapper from "../../containers/BillingWrapper";
import SettingsSidebar from "../../layouts/SettingsSidebar";
import { userSelector } from "../../store/slices/userSlice";
import { getTemplates, templatesSelector } from "../../store/slices/templatesSlice";
import { documentTemplatePath, emailTemplatePath, ERRORS_MESSAGE, MESSAGE_TYPE, smsTemplatePath, userType } from "../../constants";
import { getStringTitleCase } from "../Utils/helperFunction";
import { openSnackbar } from "../../store/slices/snackbarSlice";

const ContentGrid = ({ children }) => (
  <Grid
    container
    item
    xs={12}
    sm={9}
    md={9}
    lg={8}
    width="100%"
    justifyContent="start"
    alignItems="start"
    direction="column"
    marginLeft="20px"
  >
    {children}
  </Grid>
);

export default function UserProfile() {
  // Feature flags.
  const { documentTemplateUplift } = useFeatureFlags();
  const dispatch = useDispatch();

  const templatePathRegex = /^\/settings\/templates\/(?!sms|email|document)\w+$/;
  const { path } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const userState = useSelector(userSelector);
  const { templates } = useSelector(templatesSelector);
  const { permissions } = userState ?? {};
  const [hideSidebar, setHideSidebar] = useState(false);

  const isCommunicationTemplate = [emailTemplatePath, smsTemplatePath].includes(location.pathname);
  const isDocumentTemplate = location.pathname === documentTemplatePath;

  useEffect(() => {
    if (templatePathRegex.test(location.pathname)) {
      setHideSidebar(true);
    } else {
      setHideSidebar(false);
    }

    if (isCommunicationTemplate || isDocumentTemplate) {
      let type = "";
      switch (location.pathname) {
        case emailTemplatePath:
          type = MESSAGE_TYPE.EMAIL;
          break;
        case smsTemplatePath:
          type = MESSAGE_TYPE.SMS;
          break;
        case documentTemplatePath:
          type = MESSAGE_TYPE.DOCUMENT;
          break;
        default:
          break;
      }
      dispatch(getTemplates({ limit: 50, cursor: 1, messageType: type })).then((res) => {
        if(res?.error) {
          dispatch(openSnackbar({
            message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg,
            intent: "danger",
          }));
        }
      })
    }
  }, [location.pathname]);

  const handleEditTemplate = ({ messageType, id }) => {
    setHideSidebar(true);
    navigate(`/settings/templates/${messageType}/${id}`);
  };

  const getPageTitle = () => getStringTitleCase(location.pathname.split("/").pop());
  
  const canViewPermissions = [userType.owner, userType.master].includes(permissions?.userType);
  const canViewTemplates = permissions?.templates?.sms_view || permissions?.templates?.email_view || permissions?.templates?.document_view;

  return (
    <Grid container item height="100%" width="100%" spacing={1} direction="row">
      {!hideSidebar && (
        <Grid container item xs={12} sm={3} md={2} lg={2} width="100%" direction="column">
          <SettingsSidebar path={path} />
        </Grid>
      )}

      {path === "general" && (
        <ContentGrid>
          <UserDetails />
        </ContentGrid>
      )}

      {path === "lenders" && <ContentGrid><Lenders /></ContentGrid>}

      {canViewPermissions && path === "user" && (
        <ContentGrid>
          <BillingWrapper title="User">
            <UserRolesList />
          </BillingWrapper>
        </ContentGrid>
      )}

      {canViewTemplates &&
        path === "templates" &&
        (isCommunicationTemplate || (isDocumentTemplate && documentTemplateUplift())) && (
          <ContentGrid>
            <BillingWrapper title={`${getPageTitle()} Templates`}>
              <Templates templates={templates} onEditTemplate={handleEditTemplate} />
            </BillingWrapper>
          </ContentGrid>
        )}
    </Grid>
  );
}
